import { SpinnerDetails } from '@accounts/accounts-alerts/accounts-alerts.interface';
import { HttpParams, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Location as LocationNg } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  AnalyticsService,
  NavigationService,
  SignInComponentFieldsInfo,
} from '@frk/eds-components';
// TODO: this import can cause circular dependencies
// as in comments below, cart service needs refactored to watch for sign out events
import { CartHandlerService } from '@literature/services/cart-handler.service';
import { LOCATION, WINDOW } from '@ng-web-apis/common';
import {
  AnalyticsLogin,
  SignInInterface,
  AuthResponse,
  HiddenInput,
  ModalIntroTxt,
} from '@shared/sign-in/sign-in.interface';
import { TranslateService } from '@shared/translate/translate.service';
import {
  ACCESS,
  ADD_TO_FAV_CLICKED,
  LOCATION_LABL,
  MARKETING_FLAG,
  NEW,
  NEW_AUTH_SESSION,
  SIGNIN_PIN,
  SIGNIN_REDIRECTTOURL,
  SIGNIN_REMEMBERME,
  SIGNIN_SPINNER,
  SIGNIN_USERID,
  TOKEN_EXPIRY,
  UPGRADE_NOTNOW,
  USER_GROUP,
  IS_MFA_COMPLETE,
  TEMP_USER_COOKIE,
  AUTH_RESPONSE,
} from '@utils/app.constants';
import { CustomHttpParamEncoder } from '@utils/custom-http-param-encoder';
import { removeParam } from '@utils/link-utils';
import { Logger } from '@utils/logger';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Segment, SegmentId } from '../types/segment.type';
import { AppStateService } from './app-state.service';
import {
  IUserProfileInfo,
  LoginSource,
  ProfileSummary,
} from './profile.interface';
import { ProfileService } from './profile.service';
import { SegmentService } from './segment.service';
import { SessionService } from './session.service';
import { SiteConfigService } from './site-config.service';
import { StorageService } from './storage.service';

const logger = Logger.getLogger('SignInService');

@Injectable({
  providedIn: 'root',
})
export class SignInService {
  private spinnerDetails: SpinnerDetails = {
    spinnerName: SIGNIN_SPINNER,
    spinnerLoadingText: this.translateService.instant('signin.signinSpinner'),
  };

  private showSignInModalSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );

  private showSignInCustomIntroModalSubject$: BehaviorSubject<ModalIntroTxt> = new BehaviorSubject<ModalIntroTxt>(
    { customLabel: '', showMsg: false }
  );

  private showSignInCustomContentModalSubject$: BehaviorSubject<string> = new BehaviorSubject<string>(
    ''
  );

  private readonly spinner$: Subject<SpinnerDetails>;

  constructor(
    @Inject(WINDOW) readonly windowRef: Window,
    private sessionService: SessionService,
    private cartService: CartHandlerService,
    private appStateService: AppStateService,
    private analyticsService: AnalyticsService,
    private storageService: StorageService,
    private segmentService: SegmentService,
    private translateService: TranslateService,
    private profileService: ProfileService,
    private navService: NavigationService,
    private siteConfig: SiteConfigService,
    private locationNg: LocationNg,
    @Inject(LOCATION) readonly locationRef: Location
  ) {
    this.spinner$ = new Subject<SpinnerDetails>();
  }

  public getSpinner$(): Subject<SpinnerDetails> {
    return this.spinner$;
  }

  /**
   * Function to post sign in API call.
   */
  public onSubmit(
    submitObject: AnalyticsLogin,
    downloadLocation?: string
  ): void {
    logger.debug('onSubmit for Signin', submitObject);
    this.spinnerDetails.spinnerLoadingText = this.translateService.instant(
      'signin.signinSpinner'
    );
    this.spinnerDetails.spinnerStatus = true;
    this.spinner$.next(this.spinnerDetails);
    // US Accounts - MyFunds and Canada goes direct
    this.locationNg.replaceState(
      this.removeParamsFromUrl(
        // WDE-3495 add url hash if it exists too
        this.locationRef.pathname +
          this.locationRef.search +
          this.locationRef.hash
      )
    );
    if (this.appStateService.isOktaLogin()) {
      this.submitOkta(submitObject);
    } else {
      this.onSubmitUs(submitObject, downloadLocation);
    }
  }

  public trackLogin(event: Event): void {
    if (this.appStateService.getAuthenticationType() === 'canada') {
      // NGC-15359 - login tracking for Canada
      const target = event.target as HTMLElement;
      const isLogInButton =
        (target instanceof HTMLButtonElement ||
          target.parentElement instanceof HTMLButtonElement) &&
        (target.classList.contains('sign-in__sign-in-btn') ||
          target.parentElement.classList.contains('sign-in__sign-in-btn'));

      if (isLogInButton) {
        this.analyticsService.trackEvent({
          event: 'login',
          name: 'login',
          method: 'site',
          page_location: this.windowRef.location.href,
        });
      }
    }
  }

  public getHiddenInputs(noRedirect?: boolean): [HiddenInput?, HiddenInput?] {
    if (this.appStateService.getAuthenticationType() === 'myfunds') {
      return [
        { name: 'locale', value: this.appStateService.getMyFundsLocale() },
        {
          name: 'segment',
          value:
            this.segmentService.getCurrentSegmentId() === SegmentId.INVESTOR
              ? 'INVESTOR'
              : 'ADVISOR',
        },
      ];
    }
    if (this.appStateService.getAuthenticationType() === 'canada') {
      if (noRedirect) {
        return [{ name: 'sign-in-or-register', value: 'sign-in-or-register' }];
      }
      return [
        { name: 'sign-in-or-register', value: 'sign-in-or-register' },
        {
          name: 'redirectToURL',
          value: this.removeParamsFromUrl(this.locationRef.href),
        },
      ];
    }
    return [];
  }

  public getLoginUrl(): string {
    return this.sessionService.getLoginUrl();
  }

  private onSubmitUs(
    submitObject: AnalyticsLogin,
    downloadLocation?: string
  ): void {
    logger.debug('Entering onSubmitUs', submitObject);
    const signInForm: FormGroup = submitObject.signInForm;

    const activeDownloadLocationKey = 'current_download_location';

    const loginFormData = new HttpParams({
      encoder: new CustomHttpParamEncoder(),
    })
      .set(SIGNIN_USERID, signInForm?.value.userId)
      .set(SIGNIN_PIN, signInForm?.value.password)
      .set(SIGNIN_REMEMBERME, signInForm?.value?.rememberMe)
      .set(SIGNIN_REDIRECTTOURL, this.locationRef.href)
      .set(MARKETING_FLAG, NEW);

    this.analyticsService.trackEvent({
      event: 'login_attempt',
      location: submitObject.analyticsKey,
      link_url: this.getLoginUrl(),
    });

    if (
      downloadLocation &&
      downloadLocation !== 'undefined' &&
      downloadLocation !== '#'
    ) {
      this.storageService.store<string>(
        activeDownloadLocationKey,
        downloadLocation
      );
    }

    logger.debug('Triggering Authenticate Service for US', submitObject);
    this.sessionService.authenticateUser$(loginFormData.toString()).subscribe(
      // TODO: refactor success callback as separate private function
      (response: HttpResponse<any>) => {
        if (
          response &&
          response.status === 201 &&
          response.headers.get(LOCATION_LABL)
        ) {
          if (this.storageService.getDeviceRecognizeStatus() && response.body) {
            const body: AuthResponse = {
              redirectiveUrl: response.headers.get(LOCATION_LABL),
              userId: signInForm?.value.userId,
              userSysNo: this.storageService.getUserSysNumber(),
              deviceRecognizedYN: this.storageService.getDeviceRecognizeStatus(),
              securityQuestionsFlag: this.storageService.getSecurityQuestionStatus(),
              role: this.profileService.getUserProfile().profileInfo?.role,
              webExperience: this.profileService.getUserProfile().profileInfo
                ?.webExperience,
            };
            if (body.deviceRecognizedYN === 'N') {
              // Mapping profile summary to store it in case profile is set from bypass
              const profileSummaryFromResponse = this.mapProfileSummaryFromResponse(
                body
              );
              // We need to store correct session on marketing side before reloading the page.
              this.storageService.storeProfileSummary(
                profileSummaryFromResponse
              );
              this.redirectToMFA(body);
            }

            if (response?.body?.deviceRecognizedYN === 'Y') {
              this.navService.navigateByUrl(
                response.headers.get(LOCATION_LABL),
                '_self'
              );
            }
          } else {
            this.trackEvent('login_fail');
            logger.debug(
              'Redirecting Profile App Flows with 201 Status',
              response
            );

            this.navService.navigateByUrl(
              this.getRedirectURL(response.headers.get(LOCATION_LABL), false),
              '_self'
            );
          }
        } else {
          // Mapping profile summary to store it in case profile is set from bypass
          const profileSummaryFromResponse = this.mapProfileSummaryFromResponse(
            response.body
          );
          // We need to store correct session on marketing side before reloading the page.
          this.storageService.storeProfileSummary(profileSummaryFromResponse);
          // Check if device not recognized redirect to MFA
          if (response.body.deviceRecognizedYN === 'N') {
            this.redirectToMFA(response?.body);
          }

          // Check if device is recognized
          if (response.body.deviceRecognizedYN === 'Y') {
            // this.storageService.store<string>(NEW_AUTH_SESSION, TRUE);
            this.trackEvent('login');
            logger.debug('US Login Success for ', response?.body?.userSysNo);
            this.storageService.storeIsLoggedInSession(true);

            // sets segment based on mapped profile user
            this.saveSegmentIntoStorage(response.body.role);

            // Login in from Home page and dashboardUrl is available means, need to redirect accounts dashboard.
            if (
              this.appStateService.isHomePage() &&
              response.body.accountsAccess === ACCESS &&
              response.body.dashboardUrl
            ) {
              logger.debug(
                'Redirecting Dashboard for Accounts User',
                response?.body?.userSysNo
              );
              this.navService.navigateByUrl(
                response.body.dashboardUrl,
                '_self'
              );
            } else {
              logger.debug(
                'Reload the page for Marketing User',
                response?.body?.userSysNo
              );
              // Remove params from URL during login procedure
              const reloadUrl: string = this.removeParamsFromUrl(
                this.locationRef.href
              );
              this.windowRef.location.replace(reloadUrl);
            }
          }
        }
      },
      (exception: any) => {
        this.spinnerDetails.spinnerStatus = false;
        this.spinner$.next(this.spinnerDetails);
        logger.error('Login connection issue:', exception);
      }
    );
  }

  private submitOkta(submitObject: AnalyticsLogin): void {
    logger.debug('Entering onSubmitOkta', submitObject);
    const signInForm: FormGroup = submitObject.signInForm;
    const oktaClient = this.sessionService.getOktaClient(
      this.appStateService.getAuthenticationType()
    );
    this.storageService.saveOktaCallbackRoute(this.locationRef.pathname);
    oktaClient
      .signInWithCredentials({
        username: signInForm?.value.userId,
        password: signInForm?.value.password,
      })
      .then((transaction) => {
        if (transaction.status === 'SUCCESS') {
          oktaClient.token.getWithRedirect({
            sessionToken: transaction.sessionToken,
            responseType: 'id_token',
          });
        } else {
          logger.info('Invalid credentials');
        }
      });
  }

  /**
   *
   */
  private saveSegmentIntoStorage(role: string): void {
    const profileSegmentId: SegmentId = this.profileService.getSegmentRole(
      role
    );
    this.segmentService.setSegment(profileSegmentId, true);
  }
  /**
   * Set Profile Summary from Accounts service response
   * @param profileBodyResponse - response body
   */
  private mapProfileSummaryFromResponse(
    profileBodyResponse: IUserProfileInfo
  ): ProfileSummary {
    return {
      role: profileBodyResponse.role,
      source: LoginSource.OAUTH,
      webExperience: profileBodyResponse.webExperience,
      firm: profileBodyResponse.firm,
      accountsAccess: profileBodyResponse.accountsAccess,
      dashboardUrl: profileBodyResponse.dashboardUrl,
      // isLoggedIn: true,
    };
  }

  /**
   * Function to make sign out API call.
   */
  public signOut(segment?: Segment): void {
    this.storageService.remove(UPGRADE_NOTNOW);
    this.storageService.remove(NEW_AUTH_SESSION);
    this.storageService.remove(USER_GROUP, true);
    this.storageService.removeAuthResponseCookie();
    this.storageService.removeMFAStatusCookie();
    this.cartService.emptyCartStorage(); // FIXME cart should watch profile instead.
    this.trackEvent('logout');
    // We need to remove profile summary during logout to cleanup user experience.
    this.storageService.removeProfileSummary();
    // NGC-11585 - Need to remove bypass cookie during sign-out.
    this.storageService.removeBypassCookie();
    this.storageService.removePcsCookie();
    this.storageService.remove(ADD_TO_FAV_CLICKED, true);
    // NGC-8740 - Change logged in session during logout process to avoid loading resourceapi 2 times.
    this.storageService.storeIsLoggedInSession(false);
    this.sessionService.logout(segment);
    // TODO: should this be updating the ProfileService so it knows the user is logged out?
  }

  private trackEvent(type: string): void {
    const trackObject = {
      event: type,
      name: type,
      method: 'site',
      page_location: this.windowRef.location.href,
    };
    this.storageService
      .retrieve<boolean>(TOKEN_EXPIRY, true)
      .then((wasUserLoggedInBefore) => {
        /**
         * If a user was logged out due to token expiry and tries
         * to login again in the same session change the analytics event.
         * to re-authentication
         */
        if (wasUserLoggedInBefore) {
          if (type === 'login') {
            trackObject.event = 're-authentication';
            trackObject.event = 're-authentication';
          }
          if (type === 'login_fail') {
            trackObject.event = 're-authentication_fail';
            trackObject.event = 're-authentication_fail';
          }
        }
        this.analyticsService.trackEvent(trackObject);
        this.storageService.remove(TOKEN_EXPIRY, true);
      });
  }

  /**
   * Function to initialize sign in form.
   */
  public signInFormInit(formBuilder: FormBuilder): FormGroup {
    return formBuilder.group({
      userId: ['', [Validators.required]],
      password: ['', [Validators.required]],
      // Commented now according to NGC-8586
      // rememberMe: [false],
    });
  }

  /**
   * Set SignIn Modal Subject
   */
  public getSignInModal$(): Observable<boolean> {
    return this.showSignInModalSubject$.asObservable();
  }

  /**
   * Set SignIn Custom Intro Txt Modal Subject
   */
  public getSignInCustomIntroTxtModal$(): Observable<ModalIntroTxt> {
    return this.showSignInCustomIntroModalSubject$.asObservable();
  }

  /**
   * Get SignIn Custom Content Label for Modal Subject
   */
  public getSignInCustomContentModal$(): Observable<string> {
    return this.showSignInCustomContentModalSubject$.asObservable();
  }

  /**
   * Showing Custom intro text in sign-in modal
   * @param customLabel - custom label for Intro Text
   */
  public showCustomIntroTxtInModal(customLabel: string): void {
    this.showSignInCustomIntroModalSubject$.next({
      customLabel,
      showMsg: true,
    });
  }

  /**
   * Showing custom "body" content in sign-in modal
   * @param customLabel - custom label for "body" content Text
   */
  public showCustomContentModalModal(customLabel: string): void {
    this.showSignInCustomContentModalSubject$.next(customLabel);
  }

  /**
   * hide Favorite Fund Message In Modal
   */
  public hideCustomIntroTxtInModal(): void {
    this.showSignInCustomIntroModalSubject$.next({
      customLabel: '',
      showMsg: false,
    });
  }

  /**
   * Change SignIn Modal Subject to show modal
   */
  public showSignInModal(): void {
    this.showSignInModalSubject$.next(true);
  }

  /**
   * Change SignIn Modal Subject to hide modal
   */
  public hideSignInModal(): void {
    this.showSignInModalSubject$.next(false);
  }

  /**
   * Get component labels and required parameters
   * @param componentParams - parameters form component
   */
  public getSignInComponentFieldsInfo(
    componentParams: SignInInterface
  ): SignInComponentFieldsInfo {
    const myfundsRole =
      this.segmentService.getCurrentSegmentId() === SegmentId.INVESTOR
        ? 'investor'
        : 'advisor';

    let showLegacyContent = true;
    if (componentParams.showLegacyContent !== undefined) {
      showLegacyContent = componentParams.showLegacyContent;
    }

    // bodyContent
    let bodyContent: string;
    if (componentParams && componentParams?.signInBodyContent) {
      bodyContent = componentParams.signInBodyContent;
    } else {
      if (
        this.segmentService.getCurrentSegmentId() ===
        SegmentId.FINANCIAL_PROFESSIONALS
      ) {
        bodyContent = this.translateService.instant(
          'signin.bodyContent-advisor'
        );
      } else {
        bodyContent = this.translateService.instant('signin.bodyContent');
      }
    }

    bodyContent = bodyContent.replace('$MYFUNDS[role]', myfundsRole);

    // Below code will replace $REFURL with current page url in base64 format
    bodyContent = bodyContent.replace('$REFURL', this.getRedirectURL('', true));

    const info: SignInComponentFieldsInfo = {
      // Title labels
      title:
        componentParams.signInTitle ||
        this.translateService.instant('signin.title'),
      subTitle:
        componentParams.subTitle ||
        this.translateService.instant('signin.subTitle'),
      // Button label
      ctaText: this.translateService.instant('signin.ctaText'),
      // bodyContent
      bodyContent,
    };

    if (!this.siteConfig.hideSignInUserName()) {
      // User labels
      const userName = {
        label: this.translateService.instant('signin.userName-label'),
        placeHolder: this.translateService.instant(
          'signin.userName-placeHolder'
        ),
        helpText: this.translateService.instant('signin.userName-invalidText'),
        invalidText: this.translateService.instant(
          'signin.userName-invalidText'
        ),
      };
      info.userName = userName;

      // forgotIdLink params
      const forgotIdLink = {
        title: this.translateService.instant('signin.forgotIdLink-title'),
        url: this.translateService
          .instant('signin.forgotIdLink-url')
          .replace('$MYFUNDS[role]', myfundsRole),
      };
      info.forgotIdLink = forgotIdLink;
    }

    if (!this.siteConfig.hideSignInPassword()) {
      // Password labels
      const password = {
        label: this.translateService.instant('signin.password-label'),
        placeHolder: this.translateService.instant(
          'signin.password-placeHolder'
        ),
        helpText: this.translateService.instant('signin.password-invalidText'),
        invalidText: this.translateService.instant(
          'signin.password-invalidText'
        ),
      };
      info.password = password;

      // forgotPasswordLink params
      const forgotPasswordLink = {
        title: this.translateService.instant('signin.forgotPasswordLink-title'),
        url: this.translateService
          .instant('signin.forgotPasswordLink-url')
          .replace('$MYFUNDS[role]', myfundsRole),
      };
      info.forgotPasswordLink = forgotPasswordLink;
    }

    // Or Text params
    if (info.forgotIdLink && info.forgotPasswordLink) {
      info.orText = this.translateService.instant('signin.orText');
    }

    // accountAccessLink params
    if (showLegacyContent) {
      // account link is a part of LM legacy content
      const accountAccessLink = {
        title: this.translateService.instant('signin.accountAccessLink-title'),
        url: this.translateService.instant('signin.accountAccessLink'),
      };
      info.accountAccessLink = accountAccessLink;

      // legacyContent
      const legacyContent =
        componentParams.signInLegacyContent !== '' &&
        componentParams.signInLegacyContent
          ? componentParams.signInLegacyContent
          : this.translateService.instant('signin.legacyContent');
      info.legacyContent = legacyContent;
    }

    return info;
  }

  /**
   * create cookie string form auth response data
   * @param response - response data from auth api
   * @returns - cookie string value
   */
  private createResponseCookieString(response: AuthResponse): string {
    if (response) {
      let cookieString = '';
      const keys = [
        'userId',
        'userSysNo',
        'userGroup',
        'emailAddress',
        'deviceRecognizedYN',
        'securityQuestionsFlag',
        'targetUrl',
        'redirectiveUrl',
      ];
      Object.entries(response).forEach(([key, value]) => {
        if (keys.includes(key)) {
          if (key === 'targetUrl') {
            value = this.removeParamsFromUrl(value);
          }
          cookieString += key + '=' + value + '&';
        }
      });
      return cookieString.substring(0, cookieString.length - 1);
    }
  }

  /**
   * Redirect to MFA pags along with required cookies on profile pages
   * @param response - response data from auth api
   */
  private redirectToMFA(response: AuthResponse): void {
    // Delete auth response cookie before reset values
    this.storageService.removeAuthResponseCookie();

    // Set TEMP_USER_COOKIE for profile dashboard
    this.storageService.store(TEMP_USER_COOKIE, 'Y', false, TEMP_USER_COOKIE);

    // Set MFA Complete Flag
    this.storageService.store(IS_MFA_COMPLETE, 'N', false, IS_MFA_COMPLETE);

    // set response body into cookie for profile dashboard
    this.storageService.store(
      AUTH_RESPONSE,
      this.createResponseCookieString(response),
      false,
      AUTH_RESPONSE
    );
    logger.debug('Redirecting to passcode', response.userSysNo);
    this.navService.navigateByUrl(
      `${this.appStateService.getProfileDomain()}/profile/signin/passCode`,
      '_self'
    );
  }

  /**
   * Removes defined parameters from URL
   * @param url - URL string
   */
  private removeParamsFromUrl(url: string): string {
    // Remove params from URL during login procedure
    const paramsToRemove = ['role', 'bypass'];
    let reloadUrl: string = url;
    paramsToRemove.forEach((param: string) => {
      if (reloadUrl.includes(param)) {
        reloadUrl = removeParam(reloadUrl, param);
      }
    });
    return reloadUrl;
  }

  /**
   * Returns redirect URL hash for Profile site.
   * @param url string from API response
   * @param onlyRef boolean check if only ref url is required
   * @returns final URL with current page URL in refUrl parameter
   */
  public getRedirectURL(url: string, onlyRef: boolean): string {
    const urlHash = btoa(this.locationRef.href);
    if (onlyRef) {
      logger.debug('Redirect regRefUrl:', urlHash);
      return `regRefUrl=${urlHash}`;
    }
    logger.debug('Redirect url + refUrl:', url, urlHash);
    return `${url}?refUrl=${urlHash}`;
  }
}
